import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { matchMarkdownLink, replaceMarkdownLinks } from "./link-syntax"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Fix known Twist API content issues (e.g. reference system messages). */
export function fixApiContent(content: string): string {
  const fixed = content.replace(
    /mentioned this in an (\[another thread\])/g,
    "mentioned this in $1",
  );
  // Ensure a blank line after images so following block elements (like ---)
  // are parsed correctly by the markdown parser.
  // Upgrade single newline to double newline:
  const spaced = replaceMarkdownLinks(fixed, (link, source) =>
    link.image && source[link.end] === "\n" && source[link.end + 1] !== "\n"
      ? `${source.slice(link.start, link.end)}\n`
      : null,
  );
  return dedentImageLines(breakImagesFromBlocks(spaced));
}

const BLOCK_START = /[-#>]|\d+[.)]/y;

/**
 * Insert a blank line when an image is directly followed by a block element
 * (list item, heading, hr, blockquote, numbered list with `.` or `)`) with
 * no newline between. Don't insert for plain inline text — that would break
 * blockquote context.
 */
export function breakImagesFromBlocks(markdown: string): string {
  return replaceMarkdownLinks(markdown, (link, source) => {
    if (!link.image) return null;
    BLOCK_START.lastIndex = link.end;
    return BLOCK_START.test(source) ? `${source.slice(link.start, link.end)}\n\n` : null;
  });
}

/**
 * Dedent image-starting lines that carry ≥4 leading spaces. Beyond that
 * depth the entire line lands in indented-code-block territory (at top level
 * or as a list-item continuation), so both the image markup and any trailing
 * inline copy get swallowed as code. Keeping trailing inline copy on the
 * same dedented line preserves its visual association with the image.
 *
 * Also dedent standalone image-only lines that carry leading whitespace.
 * Foreign Twist clients (mobile/desktop) often emit screenshots at deep
 * list-item indentation; once that indent climbs to ≥4 spaces inside a
 * list-item continuation, strict CommonMark treats the line as an indented
 * code block and renders `![…](…)` as literal text instead of an <img>. We
 * only touch lines whose sole content is an image reference, so
 * legitimately-formatted nested-list images render unchanged at their
 * existing indent.
 */
export function dedentImageLines(markdown: string): string {
  return markdown
    .split("\n")
    .map((line) => {
      const indent = line.match(/^[ \t]+/)?.[0];
      if (!indent) return line;
      const image = matchMarkdownLink(line, indent.length);
      if (!image?.image) return line;
      const imageOnly = /^[ \t]*$/.test(line.slice(image.end));
      return indent.length >= 4 || imageOnly ? line.slice(indent.length) : line;
    })
    .join("\n");
}
